import { captureException } from '@sentry/nextjs'
import Link from 'next/link'
import { createClient } from '../../prismicio'
import { Format, Utils } from 'common/utils'
import { getPosts, getTags } from 'common/cms-sdk'
import { TBlogPostResult, TPlanSummary } from 'common/cms-types'
import { I18NConfig } from 'next/dist/server/config-shared'
import { NextPageWithLayout } from '../../types/nextPageWithLayout'
import SeoContainer from 'components/util/containers/SeoContainer'
import { NextSeoProps } from 'next-seo'
import React, { useEffect, useRef, useState } from 'react'
import { Query } from '@prismicio/types'
import { useRouter } from 'next/router'
import BlogPostResult from 'components/blog/BlogPostResult'
import Button from 'components/forms/Button'
import PrismicContainer from 'components/util/containers/PrismicContainer'
import classNames from 'classnames'
import Strings from 'project/localisation'
import { blogStrings } from 'common/strings/blog-strings'
import BlogPostMoreResult from 'components/blog/BlogPostMoreResult'
import { blogBreadcrumb } from 'components/util/containers/Breadcrumbs'
import { ItemListElements } from 'next-seo/lib/types'
import BlogSearch from 'components/blog/BlogSearch'
import useThrottle from 'common/useThrottle'
import Head from 'next/head'
import { getPlans } from 'common/planData'
import ContactUsCard from 'components/cards/new/ContactUsCard'
import LondonVOPlusHeroCard from 'components/cards/new/LondonVOPlusHeroCard'
import { PlanSlug } from 'common/planSlugs'
import Pagination from 'components/blog/Pagination'

const PAGE_SIZE = 16
const SPLIT_RESULTS = 11
export type BlogPostType = {
  // @ts-ignore
  posts: Query<TBlogPostResult>
  tags?: string[]
  tag?: string
  plans: TPlanSummary[]
  initialSearch?: string
  noIndex?: boolean
  noFollow?: boolean
  breadcrumb?: ItemListElements[]
  totalPages?: number
  currentPage?: number
}

const BlogPage: NextPageWithLayout<BlogPostType> = ({
  breadcrumb,
  initialSearch,
  noFollow,
  noIndex,
  plans,
  posts: initialPosts,
  tag,
  tags,
  totalPages,
  currentPage = 1,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [posts, setPosts] = useState<Query<TBlogPostResult>>(initialPosts)
  const [pageNumber, setPageNumber] = useState(currentPage)
  const router = useRouter()

  useEffect(() => {
    setPosts(initialPosts)
    setPageNumber(currentPage)
  }, [initialPosts, currentPage])
  const [search, setSearch] = useState(initialSearch || '')
  const [query, setQuery] = useState(initialSearch || '')
  const initialRender = useRef(false)
  const searchItems = useThrottle(() => {
    if (query !== search) {
      setQuery(search)
    }
  }, 200)

  useEffect(() => {
    searchItems(search)
  }, [search])

  useEffect(() => {
    if (!initialRender.current) {
      initialRender.current = true
      return
    }
    fetch(false)
  }, [query])

  const fetch = (next: boolean) => {
    setIsLoading(true)
    getPosts(
      createClient(Utils.getClientContext(router)),
      next ? pageNumber + 1 : pageNumber,
      Utils.getLocaleID(`${router.locale}`),
      PAGE_SIZE,
      next ? posts : undefined,
      tag
        ?.replace('and', '&')
        .split(' ')
        .map((v) => Format.capitalizeFirstLetter(v))
        .join(' '),
      query,
    )
      .then((res) => {
        if (next) {
          setPageNumber(pageNumber + 1)
        } else {
          setPageNumber(1)
        }
        setPosts(res)
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }

  const title = `${
    tag ? `${Format.capitalizeFirstLetter(tag)} - ` : ''
  }Latest Blog Posts & StartUp News | Hoxton Mix`
  const canonicalPath = router.asPath.split('?')[0]
  const canonical = `${process.env.SITE_URL}${canonicalPath}${
    currentPage > 1 ? `?page=${currentPage}` : ''
  }`

  const seoProps: NextSeoProps = {
    description: tag
      ? `${Format.capitalizeFirstLetter(
          tag,
        )} news, events, co-working and entrepreneurship.`
      : 'A leading resource of news, events, co-working and entrepreneurship in the capitals technology sector.',
    openGraph: {
      title,
      url: canonical,
    },
    title,
  }
  return (
    <PrismicContainer>
      <SeoContainer
        noIndex={noIndex}
        noFollow={noFollow}
        canonical={canonical}
        breadcrumb={breadcrumb || blogBreadcrumb()}
        seoProps={seoProps}
      >
        <Head>
          <link
            rel='alternate'
            type='application/rss+xml'
            title='Hoxton Mix Blog RSS feed'
            href={`${process.env.SITE_URL}/blog/rss.xml`}
          />
          {currentPage > 1 && (
            <link
              rel='prev'
              href={
                currentPage === 2
                  ? `${process.env.SITE_URL}${canonicalPath}`
                  : `${process.env.SITE_URL}${canonicalPath}?page=${
                      currentPage - 1
                    }`
              }
            />
          )}
          {totalPages && currentPage < totalPages && (
            <link
              rel='next'
              href={`${process.env.SITE_URL}${canonicalPath}?page=${
                currentPage + 1
              }`}
            />
          )}
        </Head>
        <main className='position-relative'>
          <BlogSearch
            posts={search ? posts : null}
            tags={tags}
            tag={tag}
            search={search}
            setSearch={setSearch}
            onShowMoreClick={() => fetch(true)}
            isLoading={isLoading}
          />
          {!search && (
            <>
              <div
                className={'blog-results bg-grey6 pt-md-10 pt-6 pb-md-30 pb-14'}
              >
                <div className=' container-legacy px-xxl-52 px-lg-28 px-5'>
                  <h1 className={'h4 mb-md-10 mb-4'}>{blogStrings.latest}</h1>
                  {!posts.results.length && (
                    <div>There are no results matching</div>
                  )}
                  {/* Desktop grid layout */}
                  <div className='row d-none d-md-flex'>
                    {posts.results.slice(0, SPLIT_RESULTS).map((post, i) => {
                      let size: 'sm' | 'lg' | 'md' = 'sm'
                      if (i === 0) {
                        // first item is full width
                        size = 'lg'
                      } else if (i < 5) {
                        //next 4 are half width
                        size = 'md'
                      } else if (i >= 5) {
                        //next 6 are half width
                        size = 'sm'
                      }
                      return (
                        <div
                          key={post.id}
                          className={classNames(
                            'position-relative mb-md-10 mb-4',
                            {
                              'col-md-12': size === 'lg',
                              'col-md-6': size === 'md',
                              'col-xl-4 col-md-6': size === 'sm',
                            },
                          )}
                        >
                          <BlogPostResult size={size} post={post} />
                        </div>
                      )
                    })}
                  </div>
                  {/* Mobile list layout */}
                  <div className='d-md-none'>
                    {posts.results.map((post) => {
                      return <BlogPostMoreResult key={post.id} post={post} />
                    })}
                  </div>
                </div>
              </div>
              {!!posts.results.slice(SPLIT_RESULTS).length && (
                <div className='bg-white py-lg-13 py-4 d-none d-md-block'>
                  <div
                    className={
                      'blog-results container px-xxl-27 px-lg-15 px-3-half'
                    }
                  >
                    <h2 className={'h4 mb-10'}>{blogStrings.more}</h2>
                    {posts.results.slice(SPLIT_RESULTS).map((post) => {
                      return (
                        <>
                          <BlogPostMoreResult key={post.id} post={post} />
                          <hr className={'my-10'} />
                        </>
                      )
                    })}
                  </div>
                </div>
              )}
              {totalPages && totalPages > 1 && (
                <div className='container px-xxl-52 px-lg-28 px-5'>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    baseUrl={router.asPath.split('?')[0]}
                  />
                </div>
              )}
            </>
          )}
          <div className='container new-design mt-xl-38 mt-68 pb-10'>
            <div className='row gy-5 mt-10 mt-lg-22'>
              <div className='col-12 col-lg-6 '>
                <ContactUsCard />
              </div>
              <div className='col-12 col-lg-6'>
                <LondonVOPlusHeroCard
                  planSummary={
                    plans.find((v) => v.slug === PlanSlug.SCAN_LITE)!
                  }
                />
              </div>
            </div>
          </div>
        </main>
      </SeoContainer>
    </PrismicContainer>
  )
}

export async function getServerSideProps(
  context: I18NConfig & {
    locale?: string
    params: { uid: string }
    query: { page?: string }
  },
) {
  try {
    const client = createClient(context)
    const page = Math.max(1, parseInt(context.query?.page as string, 10) || 1)

    const plans = await getPlans()
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)
    const posts = await getPosts(client, page, lang, PAGE_SIZE, undefined)
    const tags = await getTags(client, lang)
    const totalPages = Math.ceil(posts.total_results_size / PAGE_SIZE)

    if (page > totalPages && page > 1) {
      return { notFound: true }
    }

    return {
      props: {
        plans,
        posts,
        tags,
        totalPages,
        currentPage: page,
      },
    }
  } catch (e) {
    captureException(e, { tags: { page: context.query?.page || '1' } })
    return {
      props: {
        post: null,
      },
    }
  }
}

export default BlogPage
